import { API_URL } from './config';

export const federationsService = {
    onboard: async (data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/onboard`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Onboarding failed");
        }
        return res.json();
    },
    getStats: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch stats");
        return res.json();
    },
    getById: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/federations/${fedId}`, { headers });
        if (!res.ok) throw new Error("Failed to fetch federation");
        return res.json();
    },
    listPending: async () => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/pending`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch pending federations");
        return res.json();
    },
    listPublic: async () => {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/federations/public`, { headers });
        if (!res.ok) throw new Error("Failed to fetch public federations");
        return res.json();
    },

    listAll: async () => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch all federations");
        return res.json();
    },
    update: async (fedId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update federation");
        return res.json();
    },
    updateStatus: async (fedId: string, status: 'active' | 'suspended' | 'pending') => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status })
        });
        if (!res.ok) throw new Error("Failed to update status");
        return res.json();
    },
    uploadDocument: async (file: File) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/upload-doc`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'X-Filename': file.name,
                'Content-Type': file.type
            },
            body: await file.arrayBuffer()
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Document upload failed");
        }
        return res.json(); // returns { id, url, filename }
    },
    getPlayers: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/players`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch players");
        return res.json();
    },
    bulkImportPlayers: async (fedId: string, players: any[]) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/players/bulk-import`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ players })
        });
        if (!res.ok) throw new Error("Failed to import players");
        return res.json();
    },
    submitLicenseRequest: async (fedId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-requests`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to submit license request");
        return res.json();
    },
    listLicenseRequests: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-requests`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch license requests");
        return res.json();
    },
    listMyLicenseRequests: async () => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/me/license-requests`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch my license requests");
        return res.json();
    },
    updateLicenseRequest: async (fedId: string, requestId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-requests/${requestId}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update license request");
        return res.json();
    },
    updatePlayerLicense: async (fedId: string, userId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/players/${userId}/license`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update player license");
        return res.json();
    },
    removePlayer: async (fedId: string, userId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/players/${userId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to remove player");
        }
        return res.json();
    },
    // License Types
    listLicenseTypes: async (fedId: string, all: boolean = false) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-types${all ? '?all=true' : ''}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to fetch license types");
        }
        return res.json();
    },
    createLicenseType: async (fedId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-types`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to create license type");
        }
        return res.json();
    },
    updateLicenseType: async (fedId: string, typeId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-types/${typeId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to update license type");
        }
        return res.json();
    },
    deleteLicenseType: async (fedId: string, typeId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/license-types/${typeId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to delete license type");
        }
        return res.json();
    },
    // Stripe Connect logic
    getStripeStatus: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/stripe/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch Stripe status");
        return res.json();
    },
    getStripeOnboardUrl: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/stripe/onboard`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch Stripe onboard URL");
        return res.json();
    },
    getTransactions: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/transactions`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch transactions");
        return res.json();
    },
    listAffiliationRequests: async (fedId: string, status?: string) => {
        const token = localStorage.getItem('token');
        let endpoint = `${API_URL}/federations/${fedId}/affiliation-requests`;
        if (status) endpoint += `?status=${encodeURIComponent(status)}`;
        const res = await fetch(endpoint, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch affiliation requests");
        return res.json();
    },
    updateAffiliationRequest: async (fedId: string, requestId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/affiliation-requests/${requestId}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update affiliation request");
        return res.json();
    },
    getAffiliatedClubs: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/federations/${fedId}/affiliated-clubs`, { headers });
        if (!res.ok) throw new Error("Failed to fetch affiliated clubs");
        return res.json();
    },
    getPublicStats: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/federations/${fedId}/public-stats`, { headers });
        if (!res.ok) throw new Error("Failed to fetch public stats");
        return res.json();
    },
    // GED (Gestion Électronique de Documents)
    getDocuments: async (fedId: string) => {
        const token = localStorage.getItem('token');
        const headers: any = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/federations/${fedId}/documents`, { headers });
        if (!res.ok) throw new Error("Failed to fetch documents");
        return res.json();
    },
    createFolder: async (fedId: string, data: { name: string, parent_id?: string | null }) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/folders`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create folder");
        return res.json();
    },
    updateFolder: async (fedId: string, folderId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/folders/${folderId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update folder");
        return res.json();
    },
    deleteFolder: async (fedId: string, folderId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/folders/${folderId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to delete folder");
        return res.json();
    },
    createDocument: async (fedId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/documents`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to create document");
        return res.json();
    },
    updateDocument: async (fedId: string, docId: string, data: any) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/documents/${docId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error("Failed to update document");
        return res.json();
    },
    deleteDocument: async (fedId: string, docId: string) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/federations/${fedId}/documents/${docId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to delete document");
        return res.json();
    }
};
